import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import { Field, FieldArray } from 'redux-form';
import { TabPane } from 'reactstrap';
import { useHasRights } from 'hooks';
import Repeat from 'shared/repeat';
import PermissionDenied from 'shared/permissionDenied';
import {
    Button, ColorPickerDialog,
    DatePickerReduxFormWrapper,
    Divider,
    Layout,
    TextFieldReduxFormWrapper,
    Typography,
} from '@hub-mono/ui';
import { normalizeDate } from '../../../../utils/formUtil';
import styled from 'styled-components';

const StyledLayout = styled(Layout)`
    padding: 1.5rem 0.6rem 1.5rem;
`;

const PhaseRow = React.memo(({ index, phase, onFieldChange, fields }) => {
    const handleIntervalChange = useCallback(
        interval => {
            if (interval === 'NONE') {
                onFieldChange({ [`${phase}.repeatTimes`]: 0 });
            }
        },
        [onFieldChange, phase]
    );

    return (
        <Layout stack gap="--Spacing-200" key={index}>
            <Layout gap="--Spacing-200" vAlign="center">
                <ColorPickerDialog
                    value={fields.get(index).color}
                    onChange={color => onFieldChange({ [`${phase}.color`]: color })}
                />

                <Field
                    name={`${phase}.name`}
                    label="Phase Title"
                    placeholder="Phase Title"
                    component={TextFieldReduxFormWrapper}
                />

                <Field
                    name={`${phase}.start`}
                    label="Start date"
                    component={DatePickerReduxFormWrapper}
                    normalize={normalizeDate}
                />

                <Field
                    name={`${phase}.end`}
                    label="End Date"
                    component={DatePickerReduxFormWrapper}
                    normalize={normalizeDate}
                />
            </Layout>

            <Repeat
                removeBorder={true}
                wrapperClass="px-0 my-0"
                title="Phase Repeat:"
                inputFieldDisabled={'NONE' === fields.get(index).interval}
                radioInputName={`${phase}.interval`}
                textInputName={`${phase}.repeatTimes`}
                onIntervalChange={handleIntervalChange}
            />

            <Layout hAlign="flex-end">
                <Button
                    variant="text"
                    onClick={() => fields.remove(index)}
                    name="Remove phase"
                    dataCy="button--remove-phase"
                >
                    Remove phase
                </Button>
            </Layout>
        </Layout>
    );
});

const renderPhases = ({ fields, onFieldChange }) => {
    return (
        <Layout stack gap={32}>
            <Layout stack gap="--Spacing-200">
                {fields.map((phase, index) => {
                    return (
                        <PhaseRow
                            key={index}
                            phase={phase}
                            fields={fields}
                            index={index}
                            onFieldChange={onFieldChange}
                        />
                    );
                })}
            </Layout>
            <Layout>
                <Button
                    onClick={() =>
                        fields.push({
                            interval: 'NONE',
                            repeatTimes: 0,
                            start: new Date(),
                            end: new Date(),
                            name: '',
                            color: '#349E71',
                        })
                    }
                    dataCy="button--add-phase"
                >
                    Add a phase
                </Button>
            </Layout>
        </Layout>
    );
};

const PhasesTab = ({ tabId, onFieldChange }) => {
    const { hasSettingPhase } = useHasRights([
        {
            rights: ['settingProjectPhases'],
            rule: 'one',
            name: 'hasSettingPhase',
        },
    ]);

    return (
        <TabPane tabId={tabId}>
            <Layout stack gap="--Spacing-200">
                {hasSettingPhase ? (
                    <>
                        <StyledLayout gap="--Spacing-150" stack>
                            <Typography variant="label" size="large">
                                Phases
                            </Typography>
                            <FieldArray name="phases" component={renderPhases} onFieldChange={onFieldChange} />
                        </StyledLayout>
                        <Divider />
                    </>
                ) : (
                    <PermissionDenied />
                )}
            </Layout>
        </TabPane>
    );
};

PhasesTab.propTypes = {
    tabId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    onFieldChange: PropTypes.func.isRequired,
};

export default PhasesTab;
